import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Artists from "@/pages/Artists";
import ArtistDetail from "@/pages/ArtistDetail";
import Dashboard from "@/pages/Dashboard";
import SongDashboard from "@/pages/SongDashboard";
import Settings from "@/pages/Settings";
import Discovery from "@/pages/Discovery";
import BudgetTrackers from "@/pages/BudgetTrackers";
import BudgetArtist from "@/pages/BudgetArtist";
import BudgetBucket from "@/pages/BudgetBucket";
import Team from "@/pages/Team";
import SetPassword from "@/pages/SetPassword";
import BudgetShare from "@/pages/BudgetShare";
import SharePage from "@/pages/SharePage";
import TourLeaderboard from "@/pages/TourLeaderboard";
import Tours from "@/pages/Tours";
import Wraps from "@/pages/Wraps";
import WrapShare from "@/pages/WrapShare";
import NotFound from "@/pages/NotFound";
import ExposureCheck from "@/pages/ExposureCheck";
import "@/App.css";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === undefined)
    return (
      <div className="grid min-h-screen place-items-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-900" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user === undefined)
    return (
      <div className="grid min-h-screen place-items-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-900" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/share/artist/:artistId" element={<SharePage />} />
          <Route path="/tour/:slug" element={<TourLeaderboard />} />
          <Route path="/invite/:token" element={<SetPassword />} />
          <Route path="/share/budget/:token" element={<BudgetShare />} />
          <Route path="/share/exposure/:campaignId" element={<ExposureCheck isPublic />} />
          <Route path="/share/wrap/:slug" element={<WrapShare />} />
          <Route path="/share/:campaignId" element={<SharePage />} />
          <Route path="/share/song/:songId" element={<SharePage />} />
          <Route path="/" element={<Protected><Artists /></Protected>} />
          <Route path="/artists/:artistId" element={<Protected><ArtistDetail /></Protected>} />
          <Route path="/songs/:songId" element={<Protected><SongDashboard /></Protected>} />
          <Route path="/campaigns/:campaignId" element={<Protected><Dashboard /></Protected>} />
          <Route path="/campaigns/:campaignId/exposure" element={<Protected><ExposureCheck /></Protected>} />
          <Route path="/settings" element={<Protected><Settings /></Protected>} />
          <Route path="/discovery" element={<Protected><Discovery /></Protected>} />
          <Route path="/tours" element={<AdminOnly><Tours /></AdminOnly>} />
          <Route path="/wraps" element={<Protected><Wraps /></Protected>} />
          <Route path="/wraps/:slug" element={<Protected><Wraps /></Protected>} />
          <Route path="/budget" element={<AdminOnly><BudgetTrackers /></AdminOnly>} />
          <Route path="/budget/:artistId" element={<AdminOnly><BudgetArtist /></AdminOnly>} />
          <Route path="/budget/:artistId/:bucket" element={<AdminOnly><BudgetBucket /></AdminOnly>} />
          <Route path="/team" element={<AdminOnly><Team /></AdminOnly>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </AuthProvider>
  );
}

export default App;
